import React, { useMemo, useEffect, useState } from "react";
import {
  Table,
  Flex,
  useDisclosure,
  TableContainer,
  HStack,
  Tooltip,
  IconButton,
  Icon,
  useColorModeValue,
  Select,
  Box,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { RiEdit2Fill } from "react-icons/ri";
import Card from "components/Card/Card.js";
import { useForm } from "react-hook-form";
import useDataTable from "hooks/useDataTable";
import { dynamicSort } from "utils/helpers";
import Header from "components/ui/table/Header";
import Body from "components/ui/table/Body";
import Pagination from "components/ui/table/Pagination";
import { Search } from "components/ui/table/Search";
import { FaPlus } from "react-icons/fa6";
import { CreatedAt } from "components/CellElements";
import { Basic } from "components/CellElements";
import { Team } from "components/CellElements";
import { ContactName } from "components/CellElements";
import { useGetPaginatedTeams } from "queries/useTeam";
import TeamModal from "./modal";

function Teams() {
  const { formMethods, table, bgButton, modal, onAdd } = useTeams();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <HStack spacing={4} mb={4} mx="6">
          <Search methods={formMethods} />
          <Box>
            <Select
              w="140px"
              placeholder=""
              {...formMethods.register("league")}
              defaultValue={localStorage.getItem("teamsLeague") || ""}
            >
              <option value="">All Leagues</option>
              <option value="NFL">NFL</option>
              <option value="XFL">XFL</option>
              <option value="CFL">CFL</option>
              <option value="UFL">UFL</option>
              <option value="NFLPA">NFLPA</option>
              <option value="College">College</option>
            </Select>
          </Box>
          {/* <Tooltip label="Add New Team" placement="top">
            <IconButton
              aria-label="add new team"
              icon={<Icon as={FaPlus} boxSize={4} />}
              colorScheme="primary"
              bg={bgButton}
              onClick={onAdd}
            />
          </Tooltip> */}
        </HStack>
        <TableContainer>
          <Table {...table.getTableProps()}>
            <Header {...table.header} />
            <Body {...table.body} />
          </Table>
        </TableContainer>
        <Pagination mt="10" {...table.pagination} />
        <TeamModal {...modal} />
      </Card>
    </Flex>
  );
}

export default Teams;

const useTeams = () => {
  const navigate = useNavigate();
  const modal = useDisclosure();
  const formMethods = useForm({
    defaultValues: {
      search: localStorage.getItem("teamsSearch") || "",
      league: localStorage.getItem("teamsLeague") || "",
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const savedLimit = localStorage.getItem("teamsLimit");
    return savedLimit ? parseInt(savedLimit, 10) : 50;
  });
  const [currentSort, setCurrentSort] = useState([["createdAt", "DESC"]]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const watchSearch = formMethods?.watch("search");
  const watchLeague = formMethods?.watch("league");

  const getTeams = useGetPaginatedTeams({
    page: currentPage,
    limit,
    search: watchSearch,
    order: currentSort,
    filters: {
      ...(watchLeague ? { league: watchLeague } : {}),
    },
  });
  const onSort = dynamicSort(["createdAt"]);
  const data = useMemo(() => getTeams.data?.data?.teams || [], [
    JSON.stringify(getTeams.data),
  ]);

  const onEdit = (team) => {
    modal.onOpen();
    setSelectedTeam(team);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: (team) => [team.name, team.city, team.logoUrl, team.league],
        Cell: ({ value: [name, city, logoUrl, league] }) => (
          <Team
            name={league === "NFLPA" ? "-" : city ? `${city} ${name}` : "-"}
            logo={logoUrl}
          />
        ),
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "League",
        accessor: (team) => [team?.league],
        Cell: ({ value: [league] }) => <Basic text={league} />,
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <CreatedAt date={value} />,
      },
      {
        accessor: "id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <HStack spacing={3}>
            <Tooltip label="View" placement="top">
              <IconButton
                aria-label="view league contact"
                icon={<Icon as={RiEdit2Fill} boxSize={5} />}
                onClick={() => onEdit(row.original)}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    []
  );

  const sortTable = useMemo(() => {
    return currentSort.map(([column, direction]) => {
      let id = column || "createdAt";
      let desc = direction === "ASC" ? false : true;
      return { id, desc };
    });
  }, [currentSort]);

  const pageCount = useMemo(() => getTeams.data?.data?.totalPages || 0, [
    getTeams.data,
  ]);

  const table = useDataTable({
    desktopHiddenColumns: [],
    mobileHiddenColumns: [],
    data,
    columns,
    initialState: {
      pageCount,
      pageIndex: currentPage - 1,
      pageSize: limit,
      sortBy: sortTable,
    },
    setter: { setCurrentPage, setLimit },
    isLoading: getTeams.isLoading,
  });

  useEffect(() => {
    getTeams.refetch({
      page: table.pagination.pageIndex,
      limit: table.pagination.pageSize,
      order: currentSort,
      search: watchSearch,
      filters: {
        ...(watchLeague ? { league: watchLeague } : {}),
      },
    });

    localStorage.setItem("teamsPage", table.pagination.pageIndex.toString());
    localStorage.setItem("teamsLimit", table.pagination.pageSize.toString());

    if (watchSearch !== undefined) {
      localStorage.setItem("teamsSearch", watchSearch || "");
    }

    if (watchLeague !== undefined) {
      console.log("watchLeague", watchLeague);
      localStorage.setItem("teamsLeague", watchLeague || "");
    }
  }, [
    table.pagination.pageIndex,
    table.pagination.pageSize,
    watchSearch,
    watchLeague,
    JSON.stringify(table.sort?.sortBy),
  ]);

  useEffect(() => {
    let sort = table.sort?.sortBy?.map((s) => {
      let id = s.id || "createdAt";
      let desc = s.desc == false ? "ASC" : "DESC";
      return [id, desc];
    });

    setCurrentSort(sort);
  }, [JSON.stringify(table.sort?.sortBy)]);

  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  return {
    bgButton,
    formMethods,
    table,
    navigate,
    onAdd: () => {
      setSelectedTeam(null);
      modal.onOpen();
    },
    modal: {
      ...modal,
      team: selectedTeam,
    },
  };
};
